// src/views/contasContabeis/ContaContabilModal.jsx
import React, { useState, useEffect } from "react";

// Cada tipo carrega:
//  - "grupo": rótulo do <optgroup> visual
//  - "categoria": classe semântica (Receita/Despesa/Ativo/Passivo/Patrimonial/Outro) — exibida ao usuário
//  - "tpGrupo": natureza contábil técnica (Debito/Credito) — usada por relatórios
//  - "grpContabil": rótulo padrão do grupo contábil (usado no DRE/Demonstrativo Rec x Desp)
const TP_CONTA_OPTS = [
  { value:"ReceitaOrdinaria",       label:"Receita Ordinária",        grupo:"Receitas",          categoria:"Receita",     tpGrupo:"Credito", grpContabil:"Receitas Ordinárias" },
  { value:"ReceitaExtraordinaria",  label:"Receita Extraordinária",   grupo:"Receitas",          categoria:"Receita",     tpGrupo:"Credito", grpContabil:"Receitas Extraordinárias" },
  { value:"DespesaAdministrativa",  label:"Despesa Administrativa",   grupo:"Despesas",          categoria:"Despesa",     tpGrupo:"Debito",  grpContabil:"Despesas Administrativas" },
  { value:"DespesaOperacional",     label:"Despesa Operacional",      grupo:"Despesas",          categoria:"Despesa",     tpGrupo:"Debito",  grpContabil:"Despesas Operacionais" },
  { value:"DespesaFinanceira",      label:"Despesa Financeira",       grupo:"Despesas",          categoria:"Despesa",     tpGrupo:"Debito",  grpContabil:"Despesas Financeiras" },
  { value:"FundoReserva",           label:"Fundo de Reserva",         grupo:"Patrimonial",       categoria:"Patrimonial", tpGrupo:"Credito", grpContabil:"Fundo de Reserva" },
  { value:"AtivoCirculante",        label:"Ativo Circulante",         grupo:"Balanço",           categoria:"Ativo",       tpGrupo:"Debito",  grpContabil:"Ativo Circulante" },
  { value:"AtivoNaoCirculante",     label:"Ativo Não Circulante",     grupo:"Balanço",           categoria:"Ativo",       tpGrupo:"Debito",  grpContabil:"Ativo Não Circulante" },
  { value:"PassivoCirculante",      label:"Passivo Circulante",       grupo:"Balanço",           categoria:"Passivo",     tpGrupo:"Credito", grpContabil:"Passivo Circulante" },
  { value:"PassivoNaoCirculante",   label:"Passivo Não Circulante",   grupo:"Balanço",           categoria:"Passivo",     tpGrupo:"Credito", grpContabil:"Passivo Não Circulante" },
  { value:"PatrimonioLiquido",      label:"Patrimônio Líquido",       grupo:"Balanço",           categoria:"Patrimonial", tpGrupo:"Credito", grpContabil:"Patrimônio Líquido" },
  { value:"Caixa",                  label:"Caixa",                    grupo:"Controle Bancário", categoria:"Ativo",       tpGrupo:"Debito",  grpContabil:"Caixa e Equivalentes" },
  { value:"Banco",                  label:"Banco",                    grupo:"Controle Bancário", categoria:"Ativo",       tpGrupo:"Debito",  grpContabil:"Caixa e Equivalentes" },
  { value:"ContasPagar",            label:"Contas a Pagar",           grupo:"Financeiro",        categoria:"Passivo",     tpGrupo:"Credito", grpContabil:"Contas a Pagar" },
  { value:"ContasReceber",          label:"Contas a Receber",         grupo:"Financeiro",        categoria:"Ativo",       tpGrupo:"Debito",  grpContabil:"Contas a Receber" },
  { value:"Outro",                  label:"Outro",                    grupo:"Geral",             categoria:"Outro",       tpGrupo:"Debito",  grpContabil:"Outros" },
];

// Lookup rápido por value.
const TP_CONTA_INDEX = TP_CONTA_OPTS.reduce((acc, o) => { acc[o.value] = o; return acc; }, {});

// Cores visuais por categoria semântica.
const CATEGORIA_STYLE = {
  Receita:     { bg:"#DCFCE7", color:"#166534", icon:"bi-graph-up-arrow"   },
  Despesa:     { bg:"#FEE2E2", color:"#991B1B", icon:"bi-graph-down-arrow" },
  Ativo:       { bg:"#DBEAFE", color:"#1E40AF", icon:"bi-wallet2"          },
  Passivo:     { bg:"#FFEDD5", color:"#9A3412", icon:"bi-credit-card"      },
  Patrimonial: { bg:"#EDE9FE", color:"#5B21B6", icon:"bi-piggy-bank"       },
  Outro:       { bg:"#F1F5F9", color:"var(--gray-600)", icon:"bi-three-dots"       },
};

function categoriaDe(tpConta) {
  return TP_CONTA_INDEX[tpConta]?.categoria || "Outro";
}

// Default reflete o tpConta inicial (DespesaAdministrativa → Débito).
const EMPTY = {
  classificacaoContabil:"", descConta:"", natureza:"Analitica",
  tpGrupo:"Debito", grpContabil:"Despesas Administrativas",
  subGrupo:"", tpConta:"DespesaAdministrativa",
  frmRateio:"", aceitaLancamento:1, tpLancamento:"Manual",
  saldoInicial:"0.00", dtSaldoInicial:"", saldoConta:"0.00",
  banco:"", agencia:"", contaCorrente:"", chavePix:"",
  situacao:"ativo", observacao:"",
};

const isBancoCaixa = tp => ["Banco","Caixa"].includes(tp);

export default function ContaContabilModal({ show, onClose, onSave, editData, saving=false, condominioNome="" }) {
  const isEdit = !!editData?.id;
  const [form, setForm]     = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [tab, setTab]       = useState("dados");

  useEffect(() => {
    if (!show) return;
    const base = editData ? { ...EMPTY, ...editData } : { ...EMPTY };
    if (base.dtSaldoInicial?.includes("T")) base.dtSaldoInicial = base.dtSaldoInicial.split("T")[0];
    setForm(base); setErrors({}); setTab("dados");
  }, [show, editData]);

  // Conjunto de grpContabil "canônicos" usados na heurística de auto-preenchimento.
  // Se o usuário deixou customizado, NÃO sobrescreve; senão, alinha com o tpConta novo.
  const GRPS_CANONICOS = new Set(Object.values(TP_CONTA_INDEX).map(o => o.grpContabil));

  const set = (f, v) => {
    setForm(p => {
      const next = { ...p, [f]: v };
      // Sintética não aceita lançamento
      if (f === "natureza" && v === "Sintetica") next.aceitaLancamento = 0;
      if (f === "natureza" && v === "Analitica")  next.aceitaLancamento = 1;
      // Ao trocar o tipo de conta, deriva automaticamente tpGrupo e grpContabil
      // a partir da tabela canônica.
      if (f === "tpConta") {
        const cfg = TP_CONTA_INDEX[v];
        if (cfg) {
          next.tpGrupo = cfg.tpGrupo;
          // Sobrescreve grpContabil quando: (a) vazio, (b) é um valor canônico de qualquer
          // tipo (vinha do auto-fill anterior), ou (c) o tpConta antigo é da MESMA categoria
          // de outro (ex: trocar de DespesaAdm para DespesaOperacional mantém customização
          // se for diferente do canônico atual).
          if (!p.grpContabil || !p.grpContabil.trim() || GRPS_CANONICOS.has(p.grpContabil)) {
            next.grpContabil = cfg.grpContabil;
          }
        }
      }
      return next;
    });
    setErrors(p => ({ ...p, [f]: undefined }));
  };

  const catual = TP_CONTA_INDEX[form.tpConta]?.categoria || "Outro";
  const catStyle = CATEGORIA_STYLE[catual] || CATEGORIA_STYLE.Outro;

  const validate = () => {
    const e = {};
    if (!form.classificacaoContabil.trim()) e.classificacaoContabil = "Campo obrigatório";
    if (!form.descConta.trim())             e.descConta = "Campo obrigatório";
    setErrors(e);
    if (Object.keys(e).length) { setTab("dados"); return false; }
    return true;
  };

  const handleSave = () => { if (validate()) onSave({ ...form, id: editData?.id }); };

  if (!show) return null;

  const tabs = [
    { id:"dados",    label:"Dados Gerais", icon:"bi-journal-text" },
    { id:"bancario", label:"Bancário / Caixa", icon:"bi-bank", highlight: isBancoCaixa(form.tpConta) },
  ];

  // Agrupa tipos de conta
  const grupos = [...new Set(TP_CONTA_OPTS.map(o=>o.grupo))];

  return (
    <div className="modal fade show d-block" style={{ background:"rgba(0,0,0,.55)" }} tabIndex="-1">
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <div className="d-flex align-items-center gap-3">
              <div style={{ width:38,height:38,background:"rgba(255,255,255,.15)",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center" }}>
                <i className="bi bi-journal-bookmark" style={{ fontSize:18,color:"#fff" }}/>
              </div>
              <div>
                <h5 className="modal-title mb-0">{isEdit ? "Editar Conta" : "Nova Conta Contábil"}</h5>
                <small style={{ color:"rgba(255,255,255,.6)",fontSize:12 }}>{condominioNome}</small>
              </div>
            </div>
            <button className="btn-close btn-close-white" onClick={onClose}/>
          </div>

          <div style={{ background:"var(--primary)",padding:"0 24px" }}>
            <ul className="nav nav-tabs border-0" style={{ gap:4 }}>
              {tabs.map(t=>(
                <li key={t.id} className="nav-item">
                  <button className={`nav-link border-0 d-flex align-items-center gap-2 ${tab===t.id?"active":""}`}
                    style={{ background:tab===t.id?"#fff":"transparent",color:tab===t.id?"var(--primary)":"rgba(255,255,255,.7)",borderRadius:"6px 6px 0 0",fontFamily:"Poppins,sans-serif",fontSize:13,fontWeight:500,padding:"10px 18px" }}
                    onClick={() => setTab(t.id)}>
                    <i className={`bi ${t.icon}`}/>{t.label}
                    {t.highlight && <span style={{ background:"#F59E0B",color:"#fff",borderRadius:10,padding:"0 6px",fontSize:10,fontWeight:700 }}>●</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="modal-body">

            {tab==="dados" && (
              <>
                <div className="form-section">
                  <div className="form-section-title"><i className="bi bi-journal-text"/> Classificação</div>
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label">Classificação Contábil <span className="required-mark">*</span></label>
                      <input className={`form-control ${errors.classificacaoContabil?"is-invalid":""}`}
                        value={form.classificacaoContabil} onChange={e=>set("classificacaoContabil",e.target.value)}
                        placeholder="Ex: 1.1.01.001"/>
                      {errors.classificacaoContabil&&<div className="invalid-feedback">{errors.classificacaoContabil}</div>}
                      <small style={{ fontSize:11,color:"var(--gray-400)" }}>Formato: 1.1.01.001</small>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Descrição da Conta <span className="required-mark">*</span></label>
                      <input className={`form-control ${errors.descConta?"is-invalid":""}`}
                        value={form.descConta} onChange={e=>set("descConta",e.target.value)}
                        placeholder="Ex: Receita de Condomínio Ordinária"/>
                      {errors.descConta&&<div className="invalid-feedback">{errors.descConta}</div>}
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Natureza</label>
                      <select className="form-select" value={form.natureza} onChange={e=>set("natureza",e.target.value)}>
                        <option value="Analitica">Analítica (aceita lançamento)</option>
                        <option value="Sintetica">Sintética (agrupadora)</option>
                      </select>
                    </div>
                    <div className="col-md-5">
                      <label className="form-label">Tipo de Conta</label>
                      <select className="form-select" value={form.tpConta} onChange={e=>set("tpConta",e.target.value)}>
                        {grupos.map(g=>(
                          <optgroup key={g} label={`── ${g} ──`}>
                            {TP_CONTA_OPTS.filter(o=>o.grupo===g).map(o=>(
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      <div className="mt-1 d-flex align-items-center gap-2 flex-wrap">
                        <span style={{ background:catStyle.bg, color:catStyle.color, padding:"2px 10px", borderRadius:10, fontSize:11, fontWeight:700, display:"inline-flex", alignItems:"center", gap:4 }}>
                          <i className={`bi ${catStyle.icon}`}/>Categoria: {catual}
                        </span>
                        <small style={{ fontSize:11,color:"var(--gray-400)" }}>
                          Natureza contábil: <strong>{form.tpGrupo === "Credito" ? "Crédito" : "Débito"}</strong> (definida pelo tipo)
                        </small>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Natureza Contábil <small style={{ fontSize:11,color:"var(--gray-400)" }}>(Db/Cr)</small></label>
                      <select className="form-select" value={form.tpGrupo} onChange={e=>set("tpGrupo",e.target.value)}>
                        <option value="Debito">Débito</option>
                        <option value="Credito">Crédito</option>
                      </select>
                      <small style={{ fontSize:11,color:"var(--gray-400)" }}>
                        <i className="bi bi-info-circle me-1"/>Definida pelo Tipo de Conta. Só altere se houver exigência contábil específica.
                      </small>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Situação</label>
                      <select className="form-select" value={form.situacao} onChange={e=>set("situacao",e.target.value)}>
                        <option value="ativo">Ativo</option>
                        <option value="inativo">Inativo</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-section-title"><i className="bi bi-diagram-2"/> Agrupamento e Rateio</div>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Grupo Contábil</label>
                      <input className="form-control" value={form.grpContabil} onChange={e=>set("grpContabil",e.target.value)} placeholder="Ex: Receitas de Condomínio"/>
                      <small style={{ fontSize:11,color:"var(--gray-400)" }}>
                        <i className="bi bi-info-circle me-1"/>Sugerido pelo Tipo de Conta — usado no Demonstrativo Rec x Desp.
                      </small>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Sub-grupo</label>
                      <input className="form-control" value={form.subGrupo} onChange={e=>set("subGrupo",e.target.value)} placeholder="Ex: Receita Ordinária"/>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Forma de Rateio</label>
                      <input className="form-control" value={form.frmRateio} onChange={e=>set("frmRateio",e.target.value)} placeholder="Ex: Por fração ideal"/>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-section-title"><i className="bi bi-cash-coin"/> Lançamento e Saldos</div>
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label">Tipo de Lançamento</label>
                      <select className="form-select" value={form.tpLancamento} onChange={e=>set("tpLancamento",e.target.value)} disabled={!form.aceitaLancamento}>
                        <option value="Manual">Manual</option>
                        <option value="Automatico">Automático</option>
                      </select>
                      {!form.aceitaLancamento && <small style={{ color:"#EA580C",fontSize:11 }}>Conta sintética — não aceita lançamento</small>}
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Saldo Inicial (R$)</label>
                      <div className="input-group">
                        <span className="input-group-text">R$</span>
                        <input className="form-control" type="number" step="0.01" value={form.saldoInicial} onChange={e=>set("saldoInicial",e.target.value)}/>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Data do Saldo Inicial</label>
                      <input type="date" className="form-control" value={form.dtSaldoInicial} onChange={e=>set("dtSaldoInicial",e.target.value)}/>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Saldo Atual (R$)</label>
                      <div className="input-group">
                        <span className="input-group-text">R$</span>
                        <input className="form-control" type="number" step="0.01"
                          value={form.saldoConta}
                          readOnly
                          style={{background:"var(--bg-page)",color:"var(--gray-600)",cursor:"not-allowed"}}/>
                      </div>
                      <small style={{fontSize:11,color:"var(--gray-400)"}}>
                        <i className="bi bi-info-circle me-1"/>Calculado automaticamente pelos lançamentos
                      </small>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Observações</label>
                      <textarea className="form-control" rows={2} value={form.observacao} onChange={e=>set("observacao",e.target.value)}/>
                    </div>
                  </div>
                </div>
              </>
            )}

            {tab==="bancario" && (
              <>
                {!isBancoCaixa(form.tpConta) && (
                  <div style={{ background:"#FFF7ED",border:"1px solid #FED7AA",borderRadius:8,padding:"12px 16px",marginBottom:16,fontSize:13,color:"#C2410C" }}>
                    <i className="bi bi-exclamation-triangle me-2"/>
                    Esta aba é recomendada para contas do tipo <strong>Banco</strong> ou <strong>Caixa</strong>. O tipo atual é <strong>{TP_CONTA_OPTS.find(o=>o.value===form.tpConta)?.label}</strong>.
                  </div>
                )}
                <div className="form-section">
                  <div className="form-section-title"><i className="bi bi-bank"/> Dados Bancários</div>
                  <div className="row g-3">
                    <div className="col-md-5">
                      <label className="form-label">Nome do Banco</label>
                      <input className="form-control" value={form.banco} onChange={e=>set("banco",e.target.value)} placeholder="Ex: Banco do Brasil, Itaú..."/>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Agência</label>
                      <input className="form-control" value={form.agencia} onChange={e=>set("agencia",e.target.value)} placeholder="0000-0"/>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Conta Corrente</label>
                      <input className="form-control" value={form.contaCorrente} onChange={e=>set("contaCorrente",e.target.value)} placeholder="00000-0"/>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Chave PIX</label>
                      <input className="form-control" value={form.chavePix} onChange={e=>set("chavePix",e.target.value)} placeholder="CPF, CNPJ, e-mail, telefone ou chave aleatória"/>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer d-flex justify-content-between">
            <div style={{ fontSize:12,color:"var(--gray-400)" }}>
              <i className="bi bi-asterisk me-1" style={{ color:"#ef4444",fontSize:9 }}/>Campos obrigatórios
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary" onClick={onClose}><i className="bi bi-x-circle"/> Cancelar</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <><span className="spinner-border spinner-border-sm me-2"/>Salvando...</> : <><i className="bi bi-check-circle"/> {isEdit?"Salvar Alterações":"Cadastrar"}</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
